import { useQuery } from '@tanstack/react-query'
import { Table, Input, Button, message, Pagination } from 'antd'
import _ from 'lodash'
import { Search } from 'lucide-react'
import moment from 'moment'
import React, { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'

import Layout from '@components/Layout/index.jsx'
import ErrorModalWithTimer from '@components/Modal/ErrorModalWithTimer/index.jsx'
import SuccessModalWithTimer from '@components/Modal/SuccessModalWithTimer/index.jsx'
import ViewProductionWorkersModal from '@components/Modal/viewProductionWorkerModal.jsx'
import { columnProductionWorkersDone } from '@utils/columns.jsx'
import { fetchProductionWorkersDone } from '@utils/fetchData.js'

import './table.css'

import { debouncedSet } from '@utils/debounce.js'
import { handleMarshrutWorkers } from '@utils/navigateToPdf.jsx'
import { changePageSize, changePagination } from '@utils/pagination'
import { switchBgColor } from '@utils/switchBgProductionsTables.js'
import SelectServiceModal from '@components/Modal/SelectServiceModal/index.jsx'

const ProductionManagementDone = () => {
	const { t } = useTranslation()

	const formattedDate = moment().format('YYYY-MM-DD')

	const { roleNames, getMe } = useSelector((state) => state.main)
	const serviceCodes = getMe?.workerServiceCodes?.split(',') || []
	const workerDeps = getMe?.workerDeps?.split(',') || []

	const successRef = useRef()
	const errorRef = useRef()

	const [currentPage, setCurrentPage] = useState(0)
	const [isModalVisible, setIsModalVisible] = useState(false)
	const [selectedRowData, setSelectedRowData] = useState(null)
	const [docNum, setDocNum] = useState('')
	const [startDate, setStartDate] = useState('')
	const [endDate, setEndDate] = useState(formattedDate)
	const [pageSize, setPageSize] = useState(10)
	const [inputDocNum, setInputDocNum] = useState('')

	const [selectServiceModal, setSelectServiceModal] = useState(false)
	const [selectedService, setSelectedService] = useState('')

	const { data: productionWorkersData = [], isLoading } = useQuery({
		queryKey: [
			'productionWorkersDone',
			currentPage,
			pageSize,
			startDate,
			endDate,
			docNum,
		workerDeps,
			selectedService,
		],
		queryFn: fetchProductionWorkersDone,
		keepPreviousData: true,
		refetchOnWindowFocus: false,
		enabled: !!selectedService,
	})

	const handleNextPage = () => {
		if (productionWorkersData.length === 10) {
			setCurrentPage((prevPage) => prevPage + 1)
		}
	}

	const handlePreviousPage = () => {
		if (currentPage > 0) {
			setCurrentPage((prevPage) => prevPage - 1)
		}
	}

	const handleRowClick = (record) => {
		setSelectedRowData(record)
		setIsModalVisible(true)
	}

	const handleStartDate = (e) => {
		setStartDate(e.target.value)
	}

	const handleEndDate = (e) => {
		setEndDate(e.target.value)
	}

	const handleNavigateSeries = (record) => {
		console.log('record', record)
		localStorage.setItem('seriesData', JSON.stringify(record))
		const url = `/series-one-barcode`
		window.open(url, '_blank')
	}

	const handleDocNumChange = (val) => {
		debouncedSet(val, setDocNum)
	}

	const columns = columnProductionWorkersDone(
		t,
		handleRowClick,
		handleNavigateSeries,
		handleDocNumChange,
		handleMarshrutWorkers,
	)

	useEffect(() => {
		if (!getMe?.workerServiceCodes) {
			message.warning(t('youDontHaveAnyServices'))
			setSelectServiceModal(false)
		} else if (serviceCodes.length === 1) {
			setSelectServiceModal(false)
			setSelectedService(serviceCodes[0])
		} else {
			setSelectServiceModal(true)
			setSelectedService('')
		}
	}, [])

	const handleReplaceService = () => {
		// setSelectedService('')
		setSelectServiceModal(true)
	}

	const handlePaginationChange = (page, pageSize) => {
		changePagination({
			page,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}
	const handlePageSizeChange = (current, pageSize) => {
		changePageSize({
			current,
			pageSize,
			setCurrentPage,
			setPageSize,
		})
	}

	return (
		<Layout>
			<div className="m-2 md:m-5 border-[2px] border-gray-200">
				<h1 className="font-poppins ml-4 mt-6 text-xl font-bold text-black md:ml-10 md:mt-14 md:text-2xl">
					{t('finishedProductions')}
				</h1>
				<div className="mt-6 w-full border-[1px] border-[#E8E8E8] md:mt-14" />

				{/* Filters top row */}
				<div className="flex flex-col gap-4 px-4 md:px-8 mt-5 md:flex-row md:items-end">
					<div className="flex flex-col sm:flex-row items-end gap-3">
						<div className="flex flex-col gap-2 w-full sm:w-auto">
							<span className="font-bold text-sm">
								{t('docNum')}
							</span>
							<Input
								className="w-full sm:w-[250px] h-8"
								placeholder={t('docNum')}
								onChange={(e) => {
									setInputDocNum(e.target.value)
									if (e.target.value === '') setDocNum('')
								}}
								value={inputDocNum}
							/>
						</div>
						<Button
							type="primary"
							className="h-8 rounded-none"
							onClick={() => setDocNum(inputDocNum)}
							icon={<Search />}
						/>
					</div>
					<Button
						className="bg-[#0A4D68] text-white disabled:bg-gray-200 disabled:text-gray-400"
						onClick={handleReplaceService}
						disabled={
							serviceCodes.length === 0 ||
							serviceCodes.length === 1
						}
					>
						{t('replaceSelectedService')}
					</Button>
				</div>

				{/* Date filters */}
				<div className="flex mt-5 w-full flex-col md:flex-row items-start md:items-end justify-between gap-4 px-4 md:px-5">
					<div className="flex gap-4 flex-col sm:flex-row">
						<div className="flex flex-col gap-3">
							<p>{t('startDate')}</p>
							<Input
								type="date"
								value={startDate}
								className="w-full sm:w-[150px]"
								onChange={handleStartDate}
								max={endDate}
								allowClear
							/>
						</div>
						<div className="flex flex-col gap-3">
							<p>{t('endDate')}</p>
							<Input
								type="date"
								value={endDate}
								className="w-full sm:w-[150px]"
								onChange={handleEndDate}
								min={startDate}
								allowClear
							/>
						</div>
					</div>
				</div>

				{/* Pagination */}
				<div className="flex justify-end px-4 md:px-10 mt-5 mb-5">
					<Pagination
						showTotal={(total) =>
							t('totalPaginationItems', { total })
						}
						showSizeChanger={true}
						current={currentPage + 1}
						onShowSizeChange={(pageSize, current) => {
							handlePageSizeChange(current, pageSize)
						}}
						total={productionWorkersData[0]?.documentsCount || 0}
						pageSize={productionWorkersData[0]?.pageSize || 10}
						onChange={(page, pageSize) => {
							handlePaginationChange(page, pageSize)
						}}
					/>
				</div>

				{/* Table */}
				<div className="px-2 md:px-4 overflow-x-auto pb-4">
					<Table
						columns={columns}
						dataSource={productionWorkersData}
						pagination={false}
						className="mt-4 w-full md:mt-10 ant-table-override"
						rowKey={(record) => record.key}
						bordered
						scroll={{ x: 900 }}
						loading={isLoading}
						rowClassName={switchBgColor}
					/>
				</div>
			</div>

			<ViewProductionWorkersModal
				visible={isModalVisible}
				data={selectedRowData}
				onClose={() => {
					setIsModalVisible(false)
				}}
			/>
			<SuccessModalWithTimer
				getRef={(r) => {
					successRef.current = r
				}}
			/>
			<ErrorModalWithTimer
				getRef={(r) => {
					errorRef.current = r
				}}
			/>
			<SelectServiceModal
				visible={selectServiceModal}
				service={selectedService}
				handleSelect={(value) => {
					if (value) {
						setSelectedService(value)
						setSelectServiceModal(false)
					}
				}}
			/>
		</Layout>
	)
}

export default ProductionManagementDone
